import { useEffect, useRef } from 'react';
import { attemptMarkMessagesAsRead, markThreadAsRead } from '../appActions';
import { shouldShowAsUnread } from '../Chat/chatUtils';
import { connect } from '../rootReducer';
import { useRouteState } from '../routes';
import { ChatKey, getChatKeyFromRoute } from '../util/getChatKey';

/**
 * This should be a hook, but current Redux version does not support hooks
 */
export const MessageReadHandler = connect(
  (store) => ({
    userId: store.loginReducer.userId,
    shiftMap: store.listViewReducer.shiftMap,
    dayMessageMap: store.appReducer.dayMessageMap,
  }),
  (dispatch) => ({
    markThreadAsReadFrontend: (chatKey: ChatKey) => {
      dispatch(markThreadAsRead(chatKey));
    },
    markMessagesAsRead: (messageIds: number[]) =>
      dispatch(attemptMarkMessagesAsRead(messageIds)),
  })
)(({
  markThreadAsReadFrontend,
  markMessagesAsRead,
  shiftMap,
  dayMessageMap,
  userId,
}) => {
  // Keep track of messages that have been marked as read to avoid marking as read while request is ongoing.
  const markedToBackend = useRef(new Set<number>());
  const routeState = useRouteState();

  const chatKey = routeState && getChatKeyFromRoute(routeState, shiftMap);

  const prevChatKey = useRef(chatKey);

  useEffect(() => {
    if (chatKey !== null) {
      const messageIds =
        dayMessageMap[chatKey]
          ?.filter(
            (message) =>
              shouldShowAsUnread(message, userId) &&
              !markedToBackend.current.has(message.MessageId)
          )
          .map((message) => message.MessageId) ?? [];

      if (messageIds.length) {
        for (const messageId of messageIds) {
          markedToBackend.current.add(messageId);
        }
        markMessagesAsRead(messageIds).catch(() => {
          // Edge case: remove ids from the set, so it can be retried again later
          for (const messageId of messageIds) {
            markedToBackend.current.delete(messageId);
          }
        });
      }
    }
  }, [chatKey, dayMessageMap, userId, markMessagesAsRead]);

  useEffect(() => {
    if (chatKey !== prevChatKey.current && prevChatKey.current !== null) {
      markThreadAsReadFrontend(prevChatKey.current);
    }
    prevChatKey.current = chatKey;
  }, [chatKey, markThreadAsReadFrontend]);

  return null;
});
